<script setup>
import Textarea from 'primevue/textarea'

const props = defineProps({
  selectedTerritory: String,
  selectedTerritoryColor: String,
  workSuccess: Boolean,
  workedTerritories: { type: Array, default: () => [] },
  userName: String,
  workSaving: Boolean,
  workError: String,
  selectedTerritoryManzanas: { type: Array, default: () => [] },
  workedManzanas: { type: Object, default: () => ({}) },
  workedManzanasCount: { type: Number, default: 0 },
  workNotes: String,
})
defineEmits(['close', 'register', 'toggle-manzana', 'update:workNotes'])

const isManzanaWorked = (terrKey, mzIdx) => (props.workedManzanas[terrKey] || []).includes(mzIdx)
</script>

<template>
  <Transition name="sheet" :duration="{ enter: 350, leave: 300 }">
    <div v-if="selectedTerritory" class="sheet-wrapper">
      <div class="sheet-backdrop" @click="$emit('close')" />
      <div class="territory-sheet">
        <div class="drag-handle" />

        <div class="sheet-header">
          <div class="terr-dot" :style="`background:${selectedTerritoryColor}`" />
          <span class="terr-title">Territorio {{ selectedTerritory }}</span>
          <button class="sheet-close-btn" @click="$emit('close')">
            <i class="pi pi-times" />
          </button>
        </div>

        <div v-if="workSuccess" class="worked-badge">
          <i class="pi pi-check-circle worked-badge-icon" />
          <div>
            <div class="worked-badge-title">¡Trabajo registrado!</div>
            <div class="worked-badge-sub">El territorio fue marcado como hecho</div>
          </div>
        </div>

        <div v-else-if="workedTerritories.includes(selectedTerritory)" class="worked-badge">
          <i class="pi pi-check-circle worked-badge-icon" />
          <div>
            <div class="worked-badge-title">Ya registrado</div>
            <div class="worked-badge-sub">Conductor: {{ userName }}</div>
          </div>
        </div>

        <div v-else>
          <div v-if="selectedTerritoryManzanas.length" class="mz-section">
            <div class="mz-header">
              <span class="mz-label">MANZANAS</span>
              <span class="mz-count">{{ workedManzanasCount }}/{{ selectedTerritoryManzanas.length }} trabajadas</span>
            </div>
            <div class="mz-grid" :style="`--terr-color: ${selectedTerritoryColor}`">
              <button
                v-for="mzIdx in selectedTerritoryManzanas"
                :key="mzIdx"
                class="mz-chip"
                :class="isManzanaWorked(selectedTerritory, mzIdx) ? 'mz-worked' : ''"
                @click="$emit('toggle-manzana', selectedTerritory, mzIdx)"
              >{{ mzIdx }}</button>
            </div>
          </div>

          <div class="conductor-row">
            Conductor: <strong>{{ userName || '—' }}</strong>
          </div>

          <Textarea
            :modelValue="workNotes"
            @update:modelValue="$emit('update:workNotes', $event)"
            rows="2"
            class="w-full notes-input"
            placeholder="Notas opcionales..."
          />

          <button
            class="mark-done-btn"
            :disabled="workSaving"
            @click="$emit('register')"
          >
            <i :class="workSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'" />
            {{ workSaving ? 'Registrando...' : 'Registrar trabajo' }}
          </button>

          <div v-if="workError" class="work-error">{{ workError }}</div>

          <button class="cancel-btn" @click="$emit('close')">Cancelar</button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.sheet-wrapper {
  position: fixed;
  inset: 0;
  z-index: 2000;
}

.sheet-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
}

.territory-sheet {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg-grouped);
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.2);
  padding: 8px 16px;
  padding-bottom: max(env(safe-area-inset-bottom, 16px), 16px);
  max-height: 82vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

@media (min-width: 768px) {
  .territory-sheet {
    top: 0;
    left: auto;
    right: 0;
    width: 26rem;
    max-height: none;
    height: 100%;
    border-radius: 20px 0 0 20px;
  }
}

.drag-handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(var(--label-rgb), 0.25);
  margin: 4px auto 14px;
}

@media (min-width: 768px) {
  .drag-handle {
    display: none;
  }
}

.sheet-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.terr-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
}

.terr-title {
  font-size: 19px;
  font-weight: 600;
  color: var(--label-primary);
  flex: 1;
}

.sheet-close-btn {
  background: rgba(var(--label-rgb), 0.12);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(var(--label-rgb), 0.6);
  font-size: 0.75rem;
  -webkit-tap-highlight-color: transparent;
  /* Área táctil */
  position: relative;
}
.sheet-close-btn::after {
  content: '';
  position: absolute;
  inset: -8px;
}

.worked-badge {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--success-soft);
  border-radius: 14px;
  color: var(--success-deep);
  margin-bottom: 8px;
}

.worked-badge-icon {
  font-size: 1.4rem;
  flex-shrink: 0;
}

.worked-badge-title {
  font-size: 16px;
  font-weight: 600;
}

.worked-badge-sub {
  font-size: 14px;
  opacity: 0.8;
  margin-top: 2px;
}

.mz-section {
  background: var(--bg-primary);
  border-radius: 14px;
  padding: 12px;
  margin-bottom: 12px;
}

.mz-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.mz-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(var(--label-rgb), 0.5);
  letter-spacing: 0.5px;
}

.mz-count {
  font-size: 13px;
  color: rgba(var(--label-rgb), 0.5);
}

.mz-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(42px, 1fr));
  gap: 8px;
}

.mz-chip {
  height: 42px;
  border-radius: 8px;
  border: 1.5px solid var(--terr-color, rgb(156, 156, 0));
  background: color-mix(in srgb, var(--terr-color, rgb(252, 252, 71)) 18%, white);
  color: var(--terr-color, rgb(100, 100, 0));
  font-weight: 700;
  font-size: 0.92rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  transition: transform 0.08s;
}

.mz-chip:active {
  transform: scale(0.88);
}

@keyframes mz-pop {
  0%   { transform: scale(1); }
  35%  { transform: scale(1.28); }
  65%  { transform: scale(0.95); }
  100% { transform: scale(1); }
}

.mz-worked {
  background: var(--success) !important;
  border-color: var(--success) !important;
  color: #fff !important;
  animation: mz-pop 0.24s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.conductor-row {
  font-size: 15px;
  color: rgba(var(--label-rgb), 0.6);
  margin-bottom: 10px;
}

.conductor-row strong {
  color: var(--label-primary);
}

:deep(.notes-input) {
  border-radius: 12px !important;
  background: var(--bg-primary) !important;
  border: none !important;
  font-size: 16px !important;
  margin-bottom: 12px;
}

.mark-done-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px;
  border-radius: 14px;
  background: var(--success);
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 18px;
  font-weight: 600;
  transition: background 0.12s, transform 0.08s;
  -webkit-tap-highlight-color: transparent;
  min-height: 52px;
}

.mark-done-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mark-done-btn:not(:disabled):active {
  background: var(--success);
  transform: scale(0.98);
}

.work-error {
  margin-top: 8px;
  color: var(--danger);
  font-size: 14px;
  text-align: center;
}

.cancel-btn {
  display: block;
  width: 100%;
  padding: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(var(--label-rgb), 0.55);
  font-size: 16px;
  text-align: center;
  -webkit-tap-highlight-color: transparent;
  margin-top: 4px;
}

/* Transición con curva de iOS — las clases aplican a los hijos */
.sheet-enter-active .territory-sheet,
.sheet-leave-active .territory-sheet {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}
.sheet-enter-active .sheet-backdrop,
.sheet-leave-active .sheet-backdrop {
  transition: opacity 0.3s ease;
}
.sheet-enter-from .territory-sheet,
.sheet-leave-to .territory-sheet {
  transform: translateY(100%);
}
@media (min-width: 768px) {
  .sheet-enter-from .territory-sheet,
  .sheet-leave-to .territory-sheet {
    transform: translateX(100%);
  }
}
.sheet-enter-from .sheet-backdrop,
.sheet-leave-to .sheet-backdrop {
  opacity: 0;
}
</style>
